import React from 'react';
import Flexbox from 'flexbox-react';
import Utilities from '../../utils/Utilities';

const RatingCircle = ({ rating, maxScore = 5 }) => {
  const percentage = maxScore
    ? (Number(rating || 0) / maxScore) * 100
    : 0;

  return (
    <div
      className="review-rating-circle"
      style={{
        background: `conic-gradient(
          #2f6db2 ${percentage}%,
          #e8edf4 ${percentage}% 100%
        )`,
      }}
    >
      <div className="review-rating-circle-inner">
        <div className="review-rating-value">
          {Number(rating || 0).toFixed(2)}
        </div>

        <div className="review-rating-label">
          out of {maxScore}
        </div>
      </div>
    </div>
  );
};

const ReviewerBar = ({rating,type, maxScore = 5,}) => {
  const percentage = maxScore
    ? (Number(rating || 0) / maxScore) * 100
    : 0;

  return (
    <div className="reviewer-rating-bar">
      <div
        className={`reviewer-rating-progress ${type === 'self'
          ? 'reviewer-rating-self'
          : 'reviewer-rating-other'
          }`}
        style={{
          width: `${percentage}%`,
        }}
      />
    </div>
  );
};

const InsightCard = ({title, subtitle, count, items,type,}) => {
  return (
    <div className={`review-insight-card review-insight-${type}`} >
      <Flexbox alignItems="center" justifyContent="space-between" marginBottom="12px" >
        <Flexbox alignItems="center">
          <span className="review-insight-dot" />

          <span className="review-insight-title">
            {title}
          </span>
        </Flexbox>

        <span className="review-insight-count">
          {count}
        </span>
      </Flexbox>

      <div className="review-insight-subtitle">
        {subtitle}
      </div>

      <Flexbox flexWrap="wrap">
        {items.map((item, index) => (
          <div className="review-insight-item" key={`${item.name}-${index}`}>
            <span>
              {item.name}
            </span>

            {(item.score !== undefined ||
              item.gap !== undefined) && (
                <span className="review-insight-item-value">
                  {item.score !== undefined
                    ? Number(item.score).toFixed(1)
                    : Number(item.gap).toFixed(1)}
                </span>
              )}
          </div>
        ))}
      </Flexbox>
    </div>
  );
};



const UserBrief360SummaryReport = ({
  employee,
  summaryData,
}) => {
  const {
    overallRating = 0,
    maxScore = 5,
    overallLabel = '',
    insightText = '',
    reviewerRatings = [],
    strongAreas = [],
    selfHigherAreas = [],
    reviewersHigherAreas = [],
    improvementAreas = [],
    competency_insights_text
  } = summaryData || {};

  const employeeName =
    employee && employee.name
      ? employee.full_name
      : 'Employee';

  return (
    <div className="feedback-summary-report">

      <div className="review-summary-container">

        <Flexbox
          justifyContent="space-between"
          alignItems="center"
          className="review-summary-header"
        >
          <Flexbox alignItems="center">
            <div className="review-summary-icon">
              ▥
            </div>

            <div>
              <div className="review-summary-title">
                Your 360 at a glance
              </div>

              <div className="review-summary-description">
                How you see yourself, and how your peers,
                manager and CEO see you.
              </div>
            </div>
          </Flexbox>

          {/* <button
            type="button"
            className="review-hide-button"
          >
            Hide⌄
          </button> */}
        </Flexbox>

        <div className="review-summary-content">

          {/* LEFT - OVERALL SCORE */}
          <div className="review-overall-card">
            <Flexbox alignItems="center" className="review-overall-score" >
              <div className="review-overall-content">
                <Flexbox alignItems="center">
                  <RatingCircle
                    rating={overallRating}
                    maxScore={maxScore}
                  />
                  <div className="review-overall-title">
                    {overallLabel || 'Overall Performance'}
                  </div>
                </Flexbox>
                <div className="review-overall-description">
                  {insightText ||
                    'Your overall score across everyone who reviewed you.'}
                </div>

              </div>
            </Flexbox>

            {selfHigherAreas.length > 0 &&
              selfHigherAreas[0]?.name !== 'None identified' && (
                <div className="review-summary-alert">
                  <span className="review-alert-dot">
                    ●
                  </span>

                  There are {selfHigherAreas.length} areas where
                  you rate yourself higher than your reviewers do —
                  worth a chat.
                </div>
              )}

           
            {/* REVIEWER RATINGS */}
            <div className="reviewer-ratings">

              <div className="reviewer-ratings-title">
                What each group gave you
              </div>

              {reviewerRatings.map((reviewer) => (
                <Flexbox
                  key={reviewer.name}
                  alignItems="center"
                  className="reviewer-rating-row"
                >
                  <div className="reviewer-name">
                    {reviewer.name}
                  </div>

                  <ReviewerBar
                    rating={reviewer.rating}
                    type={reviewer.type}
                    maxScore={maxScore}
                  />

                  <div
                    className={`reviewer-value ${reviewer.type === 'self'
                      ? 'reviewer-value-self'
                      : 'reviewer-value-other'
                      }`}
                  >
                    {Number(reviewer.rating || 0).toFixed(2)}
                  </div>
                </Flexbox>
              ))}

            </div>
          </div>

         
          {/* RIGHT - AGREEMENT */}
          <div className="review-agreement-card">

            <div className="review-agreement-title">
              Where you and your reviewers agree —
              and where you don't
            </div>

            <div className="review-agreement-description">
              Lead with your strengths, then talk through the gaps.
            </div>

            <div className="review-insights-grid">

              <InsightCard
                title="You both agree these are strong"
                subtitle="Keep doing these — and use them to help others."
                count={`${strongAreas.length} areas`}
                items={strongAreas}
                type="strong"
              />

              <InsightCard
                title="You rate yourself higher here"
                subtitle="Ask a reviewer what they are seeing that you are not."
                count={ selfHigherAreas.length &&
                    selfHigherAreas[0]?.name !== 'None identified'
                    ? `${selfHigherAreas.length} areas`
                    : 'None'
                }
                items={ selfHigherAreas[0]?.name === 'None identified'
                    ? []
                    : selfHigherAreas
                }
                type="self-higher"
              />

              {/* REVIEWERS HIGHER */}
              <InsightCard
                title="They rate you higher here"
                subtitle={reviewersHigherAreas.length
                    ? 'These may be strengths you underestimate.'
                    : 'Nothing hidden — you already own your strengths.'
                }
                count={ reviewersHigherAreas.length
                    ? `${reviewersHigherAreas.length} areas`
                    : 'None'
                }
                items={reviewersHigherAreas}
                type="reviewer-higher"
              />
              <InsightCard
                title="Worth investing in next"
                subtitle="Pick one and set two concrete actions with your manager."
                count={`${improvementAreas.length} areas`}
                items={improvementAreas}
                type="improvement"
              />

            </div>
          </div>
        </div>




        {competency_insights_text?.length > 0 && (
          <div className="competency-insights">

            <div className="competency-insights-header">
              <div className="competency-insights-title">
                Competency insights
              </div>

              <div className="competency-insights-description">
                Ratings and feedback summary for each competency.
              </div>
            </div>

            <div className="competency-insights-table">

              {/* TABLE HEADER */}
              <div className="competency-insights-row competency-insights-header-row">

                <div className="competency-column">
                  Competency
                </div>

                <div className="insight-column">
                  Insight
                </div>

              </div>

              {/* TABLE DATA */}
              {competency_insights_text?.map((competency, index) => (
                <div
                  className="competency-insights-row"
                  key={`${competency.name}-${index}`}
                >

                  <div className="competency-column">
                    {competency.name}
                  </div>

                  <div className="insight-column">
                    {competency.text}
                  </div>

                </div>
              ))}

            </div>
          </div>
        )}






      </div>
    </div>
  );
};

export default UserBrief360SummaryReport;